export type AnalyticsConsent = 'granted' | 'denied';

const CONSENT_STORAGE_KEY = 'analytics_consent';
const REGION_DEFAULT_SET_KEY = 'analytics_region_default_applied';

export const detectGPC = (): boolean => {
  try {
    return typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl === true;
  } catch {
    return false;
  }
};

// Basic EU/EEA + UK list for GDPR/ePrivacy treatment
const EU_COUNTRIES = new Set([
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','IS','LI','NO','GB'
]);

export const getAnalyticsConsent = (): AnalyticsConsent | null => {
  try {
    // If Global Privacy Control is enabled, treat as opted-out regardless of stored value
    if (detectGPC()) return 'denied';
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (value === 'granted' || value === 'denied') return value;
    return null;
  } catch {
    return null;
  }
};

export const setAnalyticsConsent = (consent: AnalyticsConsent) => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, consent);
  } catch {}
  // Notify listeners
  try {
    const evt = new CustomEvent('analytics-consent-changed', { detail: { consent } });
    window.dispatchEvent(evt);
  } catch {}
  // Inform GA if initialized
  try {
    // Lazy import to avoid circular deps
    import('../utils/analytics')
      .then(({ setConsent, clearAnalyticsCookies }) => {
        setConsent(consent === 'granted');
        if (consent === 'denied') {
          clearAnalyticsCookies();
        }
      })
      .catch(() => {});
  } catch {}
};

export const shouldShowConsentBanner = (): boolean => {
  const current = getAnalyticsConsent();
  if (current !== null) return false;
  // If GPC is enabled, default to denied and do not show banner
  if (detectGPC()) {
    setAnalyticsConsent('denied');
    return false;
  }
  return true;
};

export const onAnalyticsConsentChange = (cb: (consent: AnalyticsConsent) => void) => {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent).detail as { consent: AnalyticsConsent };
    if (detail && (detail.consent === 'granted' || detail.consent === 'denied')) {
      cb(detail.consent);
    }
  };
  window.addEventListener('analytics-consent-changed', handler as EventListener);
  return () => window.removeEventListener('analytics-consent-changed', handler as EventListener);
};

// Ensure a region-based default is applied if no choice exists yet
export const ensureRegionDefaultConsent = async (): Promise<AnalyticsConsent | null> => {
  const existing = getAnalyticsConsent();
  if (existing !== null) return existing;
  if (detectGPC()) return 'denied';
  try {
    // Avoid reapplying within same session
    if (sessionStorage.getItem(REGION_DEFAULT_SET_KEY) === '1') return null;
  } catch {}
  try {
    const { fetchRegion } = await import('./region');
    const region = await fetchRegion();
    const code = (region.country || '').toUpperCase();
    const isEU = EU_COUNTRIES.has(code);
    const isKnown = /^[A-Z]{2}$/.test(code) && code !== 'XX';
    if (isKnown && !isEU) {
      // Default to granted outside EU/EEA/UK
      setAnalyticsConsent('granted');
      try { sessionStorage.setItem(REGION_DEFAULT_SET_KEY, '1'); } catch {}
      return 'granted';
    }
  } catch {
    // On failure, do nothing (banner will show)
  }
  return null;
};
